import type { OpenActivityArgs } from "./types";

export interface ActivityRouteRule extends OpenActivityArgs {
  parentKinds?: string[];
  parentActivity?: OpenActivityArgs;
}

/**
 * 구매 funnel(상세/원본 위에 쌓이는 전이 단계) activity kind 집합.
 *
 * 템플릿에는 커머스 funnel 이 없으므로 비어 있음. 프로젝트에서 결제/참여 같은
 * 다단계 흐름을 추가하면 여기 등록하고 `activityNav.closeFunnel(...)` 로 일괄 청소.
 */
export const PURCHASE_FUNNEL_KINDS: ReadonlySet<string> = new Set([]);

/**
 * URL → activity rule 매핑. 독립여부가 fallback 정책을 결정:
 *   - 독립=Y : `fallbackFromIndependent(currentPathname, ...)` — 호출자의 path 가 fallback.
 *             어디서 열렸든 닫혔을 때 그 곳으로 돌아간다.
 *   - 독립=N : 고정된 부모 path (같은 flow 내의 형제로 이동).
 */
export function getActivityRouteRule(
  to: string,
  currentPathname = "/",
): ActivityRouteRule | null {
  const pathname = to.split("?")[0].split("#")[0];

  // === scan ===

  // 카메라 스캔 — 독립=Y. 탭이 아니라 전체화면 activity 로 뜬다(2026-07-31 결정 —
  // 카메라 수명을 activity mount/unmount 에 맡기고, X 로 닫으면 열었던 화면 복귀).
  if (pathname === "/scan") {
    return activity("scan", to, fallbackFromIndependent(currentPathname, "/"), []);
  }

  // === scan 결과 오버레이 (2026-08-15) ===
  //
  // 스캔 결과의 "보기"/"담기"는 스캔 activity **위에 별도 activity** 로 띄운다 — 스캔
  // activity 안에 entry 로 push 하면 scan resolver 가 URL 과 무관하게 ScanPage 만
  // 렌더해 store↔URL 동기화 틈에 ScanPage 가 remount → 결과가 날아가고 라이브로
  // 복귀하던 버그의 원인이었다.
  //
  // /scan/ 네임스페이스 전용 URL 을 쓴다 (App.tsx 라우트 없음 — 컴포넌트는 ActivityHost
  // resolver 가 직접 렌더하고 식별자는 URL 에서 파싱해 prop 으로 준다). 2026-08-17 부터
  // 일반 /cards/:id·/collections/holdings/* 도 같은 kind 의 activity 라, 두 URL 은
  // "스캔 위에서 열렸는지"만 구분한다 (앱바가 X vs ←).
  //
  // fallbackPath="/" 고정: 닫힘은 아래 scan activity 로 자연 복귀하고, 이 값은 Layout
  // 베이스 탭 추론·콜드 딥링크에만 쓰인다 ("/scan" 을 주면 추론이 null 로 빠진다).
  if (/^\/scan\/cards\/\d+$/.test(pathname)) {
    return activity("cards/detail", to, "/", []);
  }
  if (pathname === "/scan/holdings/new") {
    return activity("collections/holding", to, "/", []);
  }
  // 스캔 요약 (SC-011, 2026-08-24) — 스캔 activity 위에 얹는다 (같은 이유·같은 규약)
  if (pathname === "/scan/summary") {
    return activity("scan/summary", to, "/", []);
  }

  // === 카드 / 카탈로그 (2026-08-17) ===
  //
  // 홈·검색·세트·컬렉션에서 내려가는 상세들. 전부 독립=Y — 어디서 열렸든 닫히면 호출자로
  // 복귀한다.
  //
  // ⚠ 종전에는 일반 라우트여서 activity 스택이 비어 있었고, 하드웨어 뒤로가기가
  //   resolveBack 의 main-tab-back 으로 떨어져 **어디서 열었든 홈으로 튀었다**
  //   (실기기 제보 2026-08-17). 화면은 이미 전체화면 상세처럼 보였으므로 규칙만 없었다.

  // 카드 공유 (SC-020) — 카드 상세 앱바에서 내려온다. 종속=N (상세로 복귀).
  if (/^\/cards\/\d+\/share$/.test(pathname)) {
    return activity(
      "cards/share",
      to,
      fallbackFromIndependent(currentPathname, "/"),
      ["cards/detail"],
    );
  }
  // 카드 원본 이미지 (SC-008) — 카드 상세의 이미지를 눌러 내려온다. 종속=N (상세로 복귀).
  if (/^\/cards\/\d+\/image$/.test(pathname)) {
    return activity(
      "cards/image",
      to,
      fallbackFromIndependent(currentPathname, "/"),
      ["cards/detail"],
    );
  }
  // 세부 버전 선택 (§3.3) — 독립=Y.
  if (/^\/cards\/\d+\/variant$/.test(pathname)) {
    return activity("cards/variant", to, fallbackFromIndependent(currentPathname, "/"), []);
  }
  // 카드 상세 — 스캔 결과 오버레이(/scan/cards/:id)와 **같은 kind**. 카드→카드 이동이
  // 새 activity 를 쌓는 대신 같은 스택 안에서 push 되도록 묶는다(useOverlayNavigate).
  if (/^\/cards\/\d+$/.test(pathname)) {
    return activity("cards/detail", to, fallbackFromIndependent(currentPathname, "/"), []);
  }
  // 세트 상세 — 독립=Y. 식별자가 숫자가 아닌 링크도 흡수해야 뒤로가기가 살아있다
  // (못 찾으면 화면이 "카드를 찾을 수 없음"을 그린다).
  if (/^\/sets\/[^/]+$/.test(pathname)) {
    return activity("sets/detail", to, fallbackFromIndependent(currentPathname, "/"), []);
  }
  // 묶음 상세 (SC-014) — 독립=Y. 컬렉션 탭에서도, 담기 시트에서도 열릴 수 있다.
  if (/^\/collections\/groups\/\d+$/.test(pathname)) {
    return activity(
      "collections/group",
      to,
      fallbackFromIndependent(currentPathname, "/collections"),
      [],
    );
  }
  // 묶음에 수동 추가 (SC-016) — 종속=N, 묶음 상세로 복귀한다.
  if (/^\/collections\/groups\/(\d+)\/add$/.test(pathname)) {
    const parent = `/collections/groups/${/^\/collections\/groups\/(\d+)\/add$/.exec(pathname)![1]}`;
    return activity(
      "collections/groupAdd",
      to,
      parent,
      ["collections/group"],
      "close",
      activity("collections/group", parent, "/collections", []),
    );
  }
  // 보유 저장 폼 (§6.2) — 등록(new)·수정(:id) 모두 스캔 결과의 "담기"와 같은 kind.
  if (/^\/collections\/holdings\/[^/]+$/.test(pathname)) {
    return activity(
      "collections/holding",
      to,
      fallbackFromIndependent(currentPathname, "/collections"),
      [],
    );
  }

  // === me flow ===

  // 프로필 수정 — 독립=Y
  if (pathname === "/me/profile") {
    return activity("me/profile", to, fallbackFromIndependent(currentPathname, "/me"), ["me"]);
  }
  // 설정 화면은 2026-08-24 에 없앴다 (design 2026-08-20) — 내용이 전부 내 정보로 갔다.
  // 주소만 App.tsx 가 `/me` 로 리다이렉트하므로 activity 규칙은 두지 않는다.
  // (아래 탈퇴·약관은 그때 설정 위에 쌓이던 2단계라 부모 표기는 그대로 남는다.)
  // 회원 탈퇴 — 설정의 자식. 종속=N (설정으로 복귀, 탈퇴 성공 시 페이지가 자체로
  // closeAllTo('/') 호출).
  if (pathname === "/me/withdraw") {
    return activity(
      "me/withdraw",
      to,
      "/me/settings",
      ["me/settings"],
      "close",
      activity("me/settings", "/me/settings", "/me", ["home", "me"]),
    );
  }

  // === notifications ===
  // /notifications 는 메인 탭 (SubActivity 아님). 상세만 SubActivity.
  if (/^\/notifications\/[^/]+$/.test(pathname)) {
    return activity(
      "notifications/detail",
      to,
      fallbackFromIndependent(currentPathname, "/notifications"),
      ["home", "notifications", "me"],
      "close",
    );
  }

  // === terms viewer ===

  // 약관 / 개인정보 / 마케팅 / OSS 뷰어 — 독립=Y. 어디서 열렸든 닫히면 호출자로 복귀.
  // parentKinds=[] → 모든 부모 허용 (메인 탭, auth flow, 다른 SubActivity 모두 가능).
  if (pathname === "/terms/view") {
    return activity("terms/view", to, fallbackFromIndependent(currentPathname, "/"), []);
  }

  // === support flow ===

  // 고객센터 — 독립=Y. parentKinds=[] → 어디서 열렸든 호출자 path 로 복귀.
  if (pathname === "/support") {
    return activity("support", to, fallbackFromIndependent(currentPathname, "/"), []);
  }
  // 문의 작성 — 독립=Y, 부모는 support (chain).
  if (pathname === "/support/inquiry/new") {
    return activity(
      "support/inquiry",
      to,
      fallbackFromIndependent(currentPathname, "/support?tab=inquiry"),
      ["support"],
      "close",
      activity("support", "/support?tab=inquiry", "/", []),
    );
  }
  // 문의 상세 / 수정 — 독립=N
  if (/^\/support\/inquiry\/[^/]+(?:\/edit)?$/.test(pathname)) {
    return activity(
      "support/inquiry",
      to,
      "/support?tab=inquiry",
      ["support", "support/inquiry"],
      "close",
      activity("support", "/support?tab=inquiry", "/", []),
    );
  }

  return null;
}

function activity(
  kind: string,
  url: string,
  fallbackPath: string,
  parentKinds: string[] = [],
  appBarAction: ActivityRouteRule["appBarAction"] = "close",
  parentActivity?: OpenActivityArgs,
): ActivityRouteRule {
  return {
    kind,
    presentation: "fullscreen",
    appBarAction,
    url,
    fallbackPath,
    parentKinds,
    parentActivity,
  };
}

/**
 * "독립 activity" (독립여부=Y) 용 fallback 계산.
 * 호출자의 path 를 그대로 사용 — 어디서 열렸든 닫혔을 때 그 곳으로 돌아간다.
 * currentPathname 이 비어있는 케이스 (deep-link 직접 진입 등) 만 fallback default 사용.
 */
function fallbackFromIndependent(currentPathname: string, fallback: string) {
  if (currentPathname && currentPathname !== "") return currentPathname;
  return fallback;
}
